import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Compass, FileSearch, Bug } from 'lucide-react';
import { Footer } from '../components/shared';
import { AnnounceBar } from '../components/shared/AnnounceBar';
import { SiteNav, PAGE_LINKS } from '../components/shared/SiteNav';

const recoverySteps = [
  {
    icon: FileSearch,
    title: 'URL check',
    body: 'This path may be outdated, mistyped, or moved during a deploy.',
  },
  {
    icon: Bug,
    title: 'Agent report',
    body: 'Our agents searched the logs and found no matching route definition.',
  },
  {
    icon: Compass,
    title: 'Best next move',
    body: 'Jump back to home, open the blog, or talk with us directly.',
  },
];

export const NotFoundPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>404 - Page Not Found | Briefcase AI</title>
        <meta
          name="description"
          content="The page you requested could not be found. Head back to Briefcase AI home, blog, or contact."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <AnnounceBar />

      <div className="bc-shell min-h-screen">
        <SiteNav links={PAGE_LINKS} />

        <main className="px-5 pb-24 pt-10 sm:px-8 sm:pt-16">
          <div className="mx-auto max-w-[1120px]">
            <p className="bc-eyebrow">Error 404</p>
            <h1 className="bc-h2 mt-6 max-w-[18ch]">This agent could not find that page.</h1>
            <p className="bc-prose mt-6 max-w-[62ch]">
              The path may be outdated, mistyped, or moved during a deploy. Choose a known route and
              continue from there.
            </p>

            <div className="mt-14 grid gap-10 border-t border-rule pt-10 lg:grid-cols-3 lg:gap-0">
              {recoverySteps.map((step, i) => (
                <div
                  key={step.title}
                  className={`${i === 0 ? 'lg:pr-10' : 'lg:border-l lg:border-rule lg:px-10'} ${
                    i === 2 ? 'lg:pr-0' : ''
                  }`}
                >
                  <step.icon className="h-5 w-5 text-ink-3" />
                  <p className="bc-h3 mt-4">{step.title}</p>
                  <p className="mt-3 text-[15px] leading-[1.7] text-ink-2">{step.body}</p>
                </div>
              ))}
            </div>

            <div className="mt-14 flex flex-wrap gap-3">
              <a href="/" className="bc-btn" data-analytics="click" data-cta="404-home">
                Back to home
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="https://docs.briefcaseai.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-[10px] border border-rule-strong px-[22px] py-[14px] text-[15px] font-semibold text-ink no-underline transition-opacity hover:opacity-70"
              >
                View the docs
              </a>
              <a
                href="/contact"
                className="inline-flex items-center rounded-[10px] border border-rule-strong px-[22px] py-[14px] text-[15px] font-semibold text-ink no-underline transition-opacity hover:opacity-70"
              >
                Book a Demo
              </a>
            </div>
          </div>
        </main>
      </div>

      <Footer />
    </>
  );
};
